(function () {
  'use strict';

  var TRANSITION_MS = 560;
  var TRANSITION_FALLBACK = 700;
  var WHEEL_THRESHOLD = 78;
  var WHEEL_WINDOW_MS = 130;
  var MICRO_DELTA = 3;
  var POST_ANIMATION_GUARD_MS = 180;
  var TAIL_MAX_MS = 520;
  var NEW_IMPULSE_RATIO = 1.65;
  var NEW_IMPULSE_MIN_DELTA = 75;
  var MIN_TRIGGER_GAP_MS = 220;

  function sectionId(panel) {
    if (!panel) return '';
    return panel.id || panel.dataset.sectionId || '';
  }

  function editable(target) {
    return !!target?.closest?.('input, textarea, select, button, [contenteditable="true"], [role="textbox"], dialog, [data-scrollable]');
  }

  function scrollableConsumer(target, root, direction) {
    for (var node = target; node && node !== root; node = node.parentElement) {
      var style = getComputedStyle(node);
      if (!/(auto|scroll)/.test(style.overflowY) || node.scrollHeight <= node.clientHeight + 1) continue;
      if (direction > 0 && node.scrollTop + node.clientHeight < node.scrollHeight - 1) return true;
      if (direction < 0 && node.scrollTop > 1) return true;
    }
    return false;
  }

  function createEngine(main, options) {
    options = options || {};
    var isFw = !!options.isFw;
    var panels = [];
    var currentId = '';
    var currentIndex = 0;
    var animating = false;
    var destroyed = false;
    var generation = 0;
    var fallback = 0;
    var frames = [];
    var transitionNodes = [];
    var wheelBuffer = 0;
    var wheelDirection = 0;
    var wheelStart = 0;
    var wheelTimer = 0;
    var lastTrigger = 0;
    var lastEnd = 0;
    var tailDirection = 0;
    var tailAbs = 0;
    var tailAt = 0;
    var touchY = 0;
    var abort = new AbortController();
    var signal = abort.signal;

    function livePanels() {
      return Array.from(main.querySelectorAll(isFw ? ':scope > .fw-landing__panel' : ':scope > .shopify-section'))
        .filter(function (panel) { return panel.isConnected !== false && !panel.hasAttribute('data-azur-panel-exclude'); });
    }

    function dispatch(next, previous, direction, phase) {
      window.dispatchEvent(new CustomEvent('azur:panelchange', { detail: {
        index: next, current: next, previous: previous, direction: direction,
        phase: phase || 'start', panel: panels[next] || null,
        previousPanel: panels[previous] || null, context: isFw ? 'fw-landing' : 'homepage'
      }}));
    }

    function resetWheel() {
      wheelBuffer = 0; wheelDirection = 0; wheelStart = 0;
      clearTimeout(wheelTimer); wheelTimer = 0;
    }

    function cancelTransition() {
      generation += 1;
      animating = false;
      clearTimeout(fallback); fallback = 0;
      frames.forEach(cancelAnimationFrame); frames = [];
      transitionNodes.forEach(function (item) { item.node.removeEventListener('transitionend', item.listener); });
      transitionNodes = [];
      resetWheel();
    }

    function apply(active) {
      panels.forEach(function (panel, index) {
        panel.classList.add('azur-panel');
        panel.classList.toggle('is-active', index === active);
        panel.classList.toggle('is-before', index < active);
        panel.classList.toggle('is-after', index > active);
        panel.classList.remove('is-transitioning');
        panel.style.position = 'absolute';
        panel.style.inset = '0';
        panel.style.height = 'var(--azur-panel-height, 100svh)';
        panel.style.overflow = 'hidden';
        panel.style.transition = '';
        panel.style.transform = index <= active ? 'translate3d(0,0,0)' : 'translate3d(0,var(--azur-panel-height,100svh),0)';
        panel.style.zIndex = String(index + 1);
        panel.style.willChange = '';
        panel.toggleAttribute('inert', index !== active);
        panel.setAttribute('aria-hidden', index === active ? 'false' : 'true');
      });
      main.classList.add('azur-panel-engine');
      main.style.position = 'relative';
      main.style.height = 'var(--azur-panel-height, 100svh)';
      main.style.overflow = 'hidden';
      document.body.classList.toggle('azur-panel-mode', panels.length > 1);
    }

    function refresh(preferredId) {
      if (destroyed) return;
      var oldPanels = panels;
      var oldIndex = currentIndex;
      cancelTransition();
      panels = livePanels();
      var wanted = preferredId || currentId;
      var found = panels.findIndex(function (panel) { return sectionId(panel) === wanted; });
      currentIndex = found >= 0 ? found : Math.max(0, Math.min(oldIndex, panels.length - 1));
      currentId = sectionId(panels[currentIndex]);
      oldPanels.filter(function (panel) { return !panels.includes(panel); }).forEach(cleanPanel);
      apply(currentIndex);
      if (panels.length) dispatch(currentIndex, currentIndex, 0, 'refresh');
    }

    function cleanPanel(panel) {
      panel.style.position = ''; panel.style.inset = ''; panel.style.height = '';
      panel.style.overflow = ''; panel.style.transition = ''; panel.style.transform = '';
      panel.style.zIndex = ''; panel.style.willChange = '';
      panel.classList.remove('azur-panel', 'is-active', 'is-before', 'is-after', 'is-transitioning');
      panel.removeAttribute('inert'); panel.removeAttribute('aria-hidden');
    }

    function selectBySectionId(id) {
      refresh(id);
      var index = panels.findIndex(function (panel) { return sectionId(panel) === id || panel.contains(document.getElementById(id)); });
      if (index < 0) return false;
      currentIndex = index; currentId = sectionId(panels[index]); apply(index);
      dispatch(index, index, 0, 'select');
      return true;
    }

    function goTo(next, direction) {
      if (destroyed || animating || next < 0 || next >= panels.length || next === currentIndex) return false;
      var previous = currentIndex;
      var prevPanel = panels[previous];
      var nextPanel = panels[next];
      if (!prevPanel?.isConnected || !nextPanel?.isConnected) { refresh(currentId); return false; }
      cancelTransition();
      animating = true;
      var token = generation;
      dispatch(next, previous, direction, 'start');
      panels.forEach(function (panel, index) {
        panel.classList.toggle('is-active', index === next);
        panel.toggleAttribute('inert', index !== next && index !== previous);
        panel.setAttribute('aria-hidden', index === next || index === previous ? 'false' : 'true');
      });
      var finished = false;
      function finish() {
        if (finished || destroyed || token !== generation) return;
        finished = true;
        clearTimeout(fallback); fallback = 0;
        transitionNodes.forEach(function (item) { item.node.removeEventListener('transitionend', item.listener); });
        transitionNodes = [];
        currentIndex = panels.indexOf(nextPanel);
        if (currentIndex < 0) { refresh(currentId); return; }
        currentId = sectionId(nextPanel);
        animating = false;
        apply(currentIndex);
        lastEnd = performance.now(); tailDirection = direction; tailAbs = 9999; tailAt = lastEnd;
        dispatch(currentIndex, previous, direction, 'end');
      }
      function onEnd(event) {
        if (event.propertyName === 'transform' && (event.target === prevPanel || event.target === nextPanel)) finish();
      }
      transitionNodes = [{node: prevPanel, listener: onEnd}, {node: nextPanel, listener: onEnd}];
      transitionNodes.forEach(function (item) { item.node.addEventListener('transitionend', item.listener); });
      fallback = setTimeout(finish, TRANSITION_FALLBACK);
      if (direction > 0) {
        nextPanel.style.transform = 'translate3d(0,var(--azur-panel-height,100svh),0)'; nextPanel.style.zIndex = String(panels.length + 1);
        frames.push(requestAnimationFrame(function () { if (token !== generation) return; nextPanel.classList.add('is-transitioning'); frames.push(requestAnimationFrame(function () { if (token === generation) nextPanel.style.transform = 'translate3d(0,0,0)'; })); }));
      } else {
        nextPanel.style.transform = 'translate3d(0,0,0)'; prevPanel.style.zIndex = String(panels.length + 1);
        frames.push(requestAnimationFrame(function () { if (token !== generation) return; prevPanel.classList.add('is-transitioning'); frames.push(requestAnimationFrame(function () { if (token === generation) prevPanel.style.transform = 'translate3d(0,var(--azur-panel-height,100svh),0)'; })); }));
      }
      return true;
    }

    function normalize(event) { var d = event.deltaY; if (event.deltaMode === 1) d *= 16; else if (event.deltaMode === 2) d *= innerHeight; return d; }
    function suppress(now, dir, abs) {
      if (!lastEnd) return false;
      if (now - lastEnd < POST_ANIMATION_GUARD_MS && dir === tailDirection && abs < NEW_IMPULSE_MIN_DELTA) return true;
      return now - lastEnd < TAIL_MAX_MS && now - tailAt < 120 && dir === tailDirection && !(abs >= NEW_IMPULSE_MIN_DELTA && abs > tailAbs * NEW_IMPULSE_RATIO);
    }
    function wheel(event) {
      if (event.ctrlKey || editable(event.target) || Math.abs(event.deltaX || 0) > Math.abs(event.deltaY || 0) || document.body.classList.contains('azur-mobile-menu-open')) return;
      var delta = normalize(event); var abs = Math.abs(delta); if (abs < MICRO_DELTA) return;
      var dir = delta > 0 ? 1 : -1; if (scrollableConsumer(event.target, main, dir)) return;
      if (panels.length < 2) return; event.preventDefault(); var now = performance.now();
      if (animating) { tailDirection = dir; tailAbs = abs; tailAt = now; resetWheel(); return; }
      if (suppress(now, dir, abs)) { tailDirection = dir; tailAbs = abs; tailAt = now; resetWheel(); return; }
      if (now - lastTrigger < MIN_TRIGGER_GAP_MS) { resetWheel(); return; }
      if (wheelDirection && wheelDirection !== dir) resetWheel();
      if (!wheelStart || now - wheelStart > WHEEL_WINDOW_MS) { resetWheel(); wheelStart = now; }
      wheelDirection = dir; wheelBuffer += abs; clearTimeout(wheelTimer); wheelTimer = setTimeout(resetWheel, WHEEL_WINDOW_MS);
      if (wheelBuffer >= WHEEL_THRESHOLD) { resetWheel(); lastTrigger = now; goTo(currentIndex + dir, dir); }
    }
    function key(event) {
      if (animating || editable(event.target) || document.body.classList.contains('azur-mobile-menu-open')) return;
      var dir = ['ArrowDown','PageDown',' '].includes(event.key) ? 1 : ['ArrowUp','PageUp'].includes(event.key) ? -1 : 0;
      if (dir && goTo(currentIndex + dir, dir)) event.preventDefault();
    }
    function touchStart(event) { if (!document.body.classList.contains('azur-mobile-menu-open')) touchY = event.touches[0].clientY; }
    function touchEnd(event) { var dy = touchY - event.changedTouches[0].clientY; if (Math.abs(dy) >= 45) goTo(currentIndex + (dy > 0 ? 1 : -1), dy > 0 ? 1 : -1); }
    function resize() { if (!destroyed) { cancelTransition(); apply(currentIndex); } }

    main.addEventListener('wheel', wheel, {passive:false, signal:signal});
    main.addEventListener('touchstart', touchStart, {passive:true, signal:signal});
    main.addEventListener('touchend', touchEnd, {passive:true, signal:signal});
    document.addEventListener('keydown', key, {signal:signal});
    window.addEventListener('resize', resize, {passive:true, signal:signal});
    refresh(options.initialId);

    return {
      refresh: refresh, selectBySectionId: selectBySectionId, cancelTransition: cancelTransition,
      goTo: goTo,
      state: function () { return { panels: panels.slice(), currentId: currentId, currentIndex: currentIndex, animating: animating }; },
      destroy: function () {
        if (destroyed) return; destroyed = true; cancelTransition(); abort.abort(); panels.forEach(cleanPanel);
        main.classList.remove('azur-panel-engine'); main.style.position = ''; main.style.height = ''; main.style.overflow = '';
        document.body.classList.remove('azur-panel-mode');
      }
    };
  }

  function boot() {
    var isFw = !!document.querySelector('.fw-landing--panelized');
    if (!document.body.classList.contains('template-index') && !isFw) return;
    var main = isFw ? document.querySelector('.fw-landing--panelized') : document.getElementById('MainContent');
    if (!main) return;
    var engine = createEngine(main, {isFw:isFw});
    window.__azurPanelEngine = engine;
    function owningId(target) { return target?.closest?.('.shopify-section')?.id || target?.id || ''; }
    document.addEventListener('shopify:section:unload', function (event) {
      var retainedId = owningId(event.target);
      engine.cancelTransition();
      requestAnimationFrame(function () { engine.refresh(retainedId); });
    });
    document.addEventListener('shopify:section:load', function (event) { engine.refresh(owningId(event.target)); });
    document.addEventListener('shopify:section:reorder', function () { engine.refresh(); });
    document.addEventListener('shopify:section:select', function (event) { engine.selectBySectionId(owningId(event.target)); });
    document.addEventListener('shopify:block:select', function (event) { engine.selectBySectionId(owningId(event.target)); });
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { createEngine: createEngine, sectionId: sectionId };
  else if (typeof window !== 'undefined' && typeof document !== 'undefined') boot();
})();
